import { ArrowLeft, CheckCircle2, Link2, RefreshCw, RotateCcw, Smartphone, Trash2, XCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { applyFocus } from '../hooks/useRemoteNavigation'
import { setupUrl } from '../constants'
import {
  clearTvRemoteCredentials,
  createPairingSession,
  finishPairingSession,
  getRemoteStatus,
  getTvRemoteCredentials,
  pollPairingSession,
  rotateTvToken,
  saveTvRemoteCredentials,
  unlinkTvRemote,
  type PairingSession,
  type PairingValues,
  type RemoteDeviceStatus,
  type TvRemoteCredentials,
} from '../lib/pairing'

interface Props {
  values: PairingValues
  onPair: (values: PairingValues) => void
  onClose: () => void
}

type PanelState = { kind: 'paired' | 'loading' | 'waiting' | 'ok' | 'error'; message?: string }

export function PairingPanel({ values, onPair, onClose }: Props) {
  const initialRemote = getTvRemoteCredentials()
  const [remote, setRemote] = useState<TvRemoteCredentials | null>(initialRemote)
  const [session, setSession] = useState<PairingSession | null>(null)
  const [state, setState] = useState<PanelState>(initialRemote ? { kind: 'paired' } : { kind: 'loading' })
  const [remaining, setRemaining] = useState(0)
  const [status, setStatus] = useState<RemoteDeviceStatus | null>(null)
  const [confirmUnlink, setConfirmUnlink] = useState(false)

  const refreshStatus = useCallback(async (value: TvRemoteCredentials) => {
    try { setStatus(await getRemoteStatus(value.deviceId, value.tvToken)) } catch { setStatus(null) }
  }, [])

  const create = useCallback(async () => {
    setState({ kind: 'loading' })
    setSession(null)
    setConfirmUnlink(false)
    try {
      const value = await createPairingSession(values)
      setSession(value)
      setState({ kind: 'waiting' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Pairing service unavailable.'
      if (/revoked/i.test(message)) {
        clearTvRemoteCredentials()
        setRemote(null)
      }
      setState({ kind: 'error', message })
    }
  }, [values])

  useEffect(() => {
    if (!initialRemote) create()
    else refreshStatus(initialRemote)
    // The saved credential is read once when this management panel opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handler = (event: Event) => { event.stopImmediatePropagation(); onClose() }
    window.addEventListener('vela:back', handler, true)
    return () => window.removeEventListener('vela:back', handler, true)
  }, [onClose])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const panel = document.querySelector('.pairing-panel')
      applyFocus(
        panel?.querySelector<HTMLElement>('[data-autofocus]')
        || panel?.querySelector<HTMLElement>('[data-focusable]'),
      )
    }, 60)
    return () => clearTimeout(timer)
  }, [session, state.kind, confirmUnlink])

  useEffect(() => {
    if (!session || state.kind !== 'waiting') return
    let active = true
    let timer = 0
    // A dropped packet must not end a code that is still valid for minutes.
    // Give up only after several failures in a row, or once the code expires.
    let failures = 0
    let lastError = ''
    const poll = async () => {
      try {
        const result = await pollPairingSession(session)
        if (!active) return
        failures = 0
        if (result) {
          saveTvRemoteCredentials(result.remote)
          setRemote(result.remote)
          setState({ kind: 'ok', message: 'Phone linked. It will reconnect automatically.' })
          onPair(result.values)
          refreshStatus(result.remote)
          await finishPairingSession(session).catch(() => undefined)
          return
        }
      } catch (error) {
        if (!active) return
        failures += 1
        lastError = error instanceof Error ? error.message : 'Pairing expired.'
        if (failures >= 3 || session.expiresAt <= Date.now()) {
          setState({ kind: 'error', message: lastError })
          return
        }
      }
      if (active) timer = window.setTimeout(poll, failures ? 3_000 : 1_500)
    }
    poll()
    return () => { active = false; clearTimeout(timer) }
  }, [onPair, refreshStatus, session, state.kind])

  useEffect(() => {
    if (!session) return
    const update = () => setRemaining(Math.max(0, Math.ceil((session.expiresAt - Date.now()) / 1000)))
    update()
    const timer = window.setInterval(update, 1_000)
    return () => clearInterval(timer)
  }, [session])

  async function rotate() {
    if (!remote) return
    setState({ kind: 'loading', message: 'Rotating private TV key…' })
    try {
      const next = await rotateTvToken(remote)
      setRemote(next)
      setState({ kind: 'paired', message: 'TV key rotated.' })
      refreshStatus(next)
    } catch (error) {
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not rotate the TV key.' })
    }
  }

  async function unlink() {
    if (!remote) return
    setState({ kind: 'loading', message: 'Unlinking controllers…' })
    try {
      await unlinkTvRemote(remote)
      setRemote(null)
      setStatus(null)
      setConfirmUnlink(false)
      setState({ kind: 'ok', message: 'TV unlinked. Saved controller keys were revoked.' })
    } catch (error) {
      setState({ kind: 'error', message: error instanceof Error ? error.message : 'Could not unlink this TV.' })
    }
  }

  const managing = remote && (state.kind === 'paired' || (state.kind === 'ok' && !session))

  return (
    <div className="settings-dialog pairing-panel" role="dialog" aria-label="Phone remote">
      <div className="dialog-header">
        <button className="round-button" data-focusable data-autofocus onClick={onClose} aria-label="Close pairing"><ArrowLeft size="1.75rem" /></button>
        <div><small>PHONE REMOTE</small><h2>{managing ? 'Paired forever' : 'Link a phone'}</h2></div>
      </div>
      <div className="pairing-body">
        <span className="pairing-icon">{managing ? <Link2 size="2.75rem" /> : <Smartphone size="2.75rem" />}</span>

        {managing ? (
          <div className="paired-management">
            <h3>{remote.deviceName}</h3>
            <p><span className={`remote-dot ${status?.tvOnline ? 'online' : ''}`} /> This TV reconnects automatically</p>
            <p>{status ? `${status.controllers} paired phone${status.controllers === 1 ? '' : 's'} · ${status.connectedControllers} connected now` : 'Remote status unavailable'}</p>
            {state.message ? <span className="pairing-success"><CheckCircle2 size="1.5rem" /> {state.message}</span> : null}
            <div className="paired-actions" data-nav-group="col" data-nav-key="paired-actions">
              <button className="primary-button" data-focusable onClick={create}><Smartphone size="1.5rem" /> Pair another phone</button>
              <button className="secondary-button" data-focusable onClick={rotate}><RotateCcw size="1.5rem" /> Rotate private TV key</button>
              {!confirmUnlink ? (
                <button className="secondary-button danger-button" data-focusable onClick={() => setConfirmUnlink(true)}><Trash2 size="1.5rem" /> Unlink all phones</button>
              ) : (
                <div className="unlink-confirm"><p>This revokes every phone controller. Continue?</p><button className="secondary-button danger-button" data-focusable data-autofocus onClick={unlink}>Yes, unlink all</button><button className="secondary-button" data-focusable onClick={() => setConfirmUnlink(false)}>Keep paired</button></div>
              )}
            </div>
          </div>
        ) : null}

        {state.kind === 'loading' ? <><h3>{state.message || 'Creating a private code'}</h3><RefreshCw className="spin" size="2.25rem" /></> : null}
        {session && (state.kind === 'waiting' || state.kind === 'ok') ? (
          <>
            <p>On your phone, open</p>
            <strong className="pairing-url">{setupUrl().replace(/^https?:\/\//, '').replace(/\/$/, '')}</strong>
            <p>Then enter this one-time linking code</p>
            <strong className="pairing-code">{session.code.slice(0, 4)} {session.code.slice(4)}</strong>
            {state.kind === 'waiting' ? <span className="pairing-wait"><i /> Waiting · {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}</span> : <span className="pairing-success"><CheckCircle2 size="1.75rem" /> {state.message}</span>}
          </>
        ) : null}
        {state.kind === 'error' ? <div className="pairing-error"><XCircle size="2.25rem" /><h3>Could not pair</h3><p>{state.message}</p><button className="secondary-button" data-focusable data-autofocus onClick={create}><RefreshCw size="1.5rem" /> Try again</button></div> : null}
        {!remote && !session && state.kind === 'ok' ? (
          <div className="pairing-error pairing-unlinked">
            <CheckCircle2 size="2.25rem" />
            <h3>TV unlinked</h3>
            <p>{state.message}</p>
            <button className="primary-button" data-focusable data-autofocus onClick={create}><Smartphone size="1.5rem" /> Pair a phone again</button>
          </div>
        ) : null}
        {!managing ? <p className="pairing-privacy">The code expires in ten minutes. After linking, separate private keys keep this TV and phone paired until either side unlinks.</p> : null}
      </div>
    </div>
  )
}
